import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";

const STORAGE_KEY = "gyro-hint-dismissed";

/**
 * Discreet floating hint inviting iOS users to tap once
 * to enable gyroscope-based 3D parallax on product cards.
 * Auto-hides after permission grant, dismiss, or 8s timeout.
 */
export const GyroPermissionHint = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (!isTouch) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    const DOE = (DeviceOrientationEvent as unknown) as {
      requestPermission?: () => Promise<string>;
    };
    // Only show on iOS 13+ where permission is required
    if (typeof DOE?.requestPermission !== "function") return;

    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const dismiss = () => {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setVisible(false);
    };
    window.addEventListener("touchend", dismiss, { once: true });
    const timeout = setTimeout(dismiss, 8000);
    return () => {
      window.removeEventListener("touchend", dismiss);
      clearTimeout(timeout);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-none animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-foreground/90 backdrop-blur-md text-background shadow-2xl border border-gold/30 pointer-events-auto">
        <Smartphone className="w-4 h-4 text-gold animate-pulse" />
        <span className="text-xs font-body font-medium tracking-wide">
          Touchez l'écran pour activer l'effet 3D
        </span>
        <button
          onClick={() => {
            sessionStorage.setItem(STORAGE_KEY, "1");
            setVisible(false);
          }}
          aria-label="Fermer"
          className="ml-1 opacity-60 hover:opacity-100 transition-opacity"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
